'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generateOnboardingPackPDF, WorkerProfileData } from '@/lib/onboarding-pack';
import { Download, Share2, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

interface OnboardingPackClientProps {
  workerData: {
    name: string;
    phone: string;
    email: string;
    address?: string;
    suburb?: string;
    trade: string;
    experience: string;
    abn?: string;
  };
}

export function OnboardingPackClient({ workerData }: OnboardingPackClientProps) {
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    bankName: '',
    bsb: '',
    accountNumber: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const generatePDF = async () => {
    // Validate required fields
    if (!formData.bankName || !formData.bsb || !formData.accountNumber ||
        !formData.emergencyContactName || !formData.emergencyContactPhone) {
      toast.error('Please fill in all payment and emergency contact details');
      return;
    }
    
    setLoading(true);
    
    try {
      // Combine profile data with form data
      const pdfData: WorkerProfileData = {
        ...workerData,
        ...formData
      };
      
      // Generate PDF
      const pdfBytes = await generateOnboardingPackPDF(pdfData);
      
      // Create blob and URL
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      
      toast.success('Your onboarding pack is ready');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadPDF = () => {
    if (!pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `onboarding-pack-${workerData.name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const sharePDF = async () => {
    if (!pdfUrl) return;
    
    try {
      // Fetch the blob from the URL
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const file = new File([blob], 'onboarding-pack.pdf', { type: 'application/pdf' });
      
      // Use Web Share API if available
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'My Worker Onboarding Pack',
          text: 'Here are my details for onboarding',
          files: [file]
        });
      } else {
        // Fallback: copy link to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Share link copied to clipboard');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Unable to share. Try downloading instead.');
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Profile Preview */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Profile Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Name</p>
            <p className="font-medium">{workerData.name}</p>
          </div>
          <div>
            <p className="text-gray-600">Phone</p>
            <p className="font-medium">{workerData.phone}</p>
          </div>
          <div>
            <p className="text-gray-600">Email</p>
            <p className="font-medium">{workerData.email}</p>
          </div>
          <div>
            <p className="text-gray-600">Trade</p>
            <p className="font-medium">{workerData.trade}</p>
          </div>
          <div>
            <p className="text-gray-600">Experience</p>
            <p className="font-medium">{workerData.experience}</p>
          </div>
          {(workerData.address || workerData.suburb) && (
            <div>
              <p className="text-gray-600">Location</p>
              <p className="font-medium">{workerData.address || workerData.suburb}</p>
            </div>
          )}
          {workerData.abn && (
            <div>
              <p className="text-gray-600">ABN</p>
              <p className="font-medium">{workerData.abn}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Payment Details Form */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Payment Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="bankName">Bank Name *</Label>
            <Input
              id="bankName"
              name="bankName"
              value={formData.bankName}
              onChange={handleInputChange}
              placeholder="e.g. Commonwealth Bank"
              required
            />
          </div>
          <div>
            <Label htmlFor="bsb">BSB *</Label>
            <Input
              id="bsb"
              name="bsb"
              value={formData.bsb}
              onChange={handleInputChange}
              placeholder="e.g. 062-001"
              required
            />
          </div>
          <div>
            <Label htmlFor="accountNumber">Account Number *</Label>
            <Input
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleInputChange}
              placeholder="e.g. 12345678"
              required
            />
          </div>
        </div>
      </div>
      
      {/* Emergency Contact Form */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Emergency Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="emergencyContactName">Contact Name *</Label>
            <Input
              id="emergencyContactName"
              name="emergencyContactName"
              value={formData.emergencyContactName}
              onChange={handleInputChange}
              placeholder="e.g. John Smith"
              required
            />
          </div>
          <div>
            <Label htmlFor="emergencyContactPhone">Phone Number *</Label>
            <Input
              id="emergencyContactPhone"
              name="emergencyContactPhone"
              value={formData.emergencyContactPhone}
              onChange={handleInputChange}
              placeholder="e.g. 0412 345 678"
              required
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="emergencyContactRelationship">Relationship</Label>
            <Input
              id="emergencyContactRelationship"
              name="emergencyContactRelationship"
              value={formData.emergencyContactRelationship}
              onChange={handleInputChange}
              placeholder="e.g. Partner, Parent, Friend"
            />
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={generatePDF}
          disabled={loading}
          size="lg"
          className="flex-1"
        >
          {loading ? (
            <>
              <FileText className="mr-2 h-4 w-4 animate-pulse" />
              Generating PDF...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Generate PDF
            </>
          )}
        </Button>
        
        {pdfUrl && (
          <>
            <Button
              onClick={downloadPDF}
              variant="outline"
              size="lg"
              className="flex-1"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            
            <Button
              onClick={sharePDF}
              variant="outline"
              size="lg"
              className="flex-1"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
          </>
        )}
      </div>
      
      {pdfUrl && (
        <div className="mt-4 p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-green-800">
            ✅ Your onboarding pack is ready! You can download it or share it directly with contractors.
          </p>
        </div>
      )}
    </div>
  );
}